"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ProductionFlowChart } from "@/components/production-flow-chart";
import { ProductionProgress } from "@/components/production-progress";
import { ProductionSpeedKPI } from "@/components/production-speed-kpi";
import { DailyProductivitySummary } from "@/components/daily-productivity-summary";
import { CollapsibleSidebar } from "@/components/collapsible-sidebar";
import useAuth from "@/hooks/auth/useAuth";
import { Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect } from "react";

export default function HomePage() {
  const { user, loading } = useAuth();
  const router = useRouter();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      // Redirect to login with current path as redirectTo
      router.push("/login?redirectTo=/");
    }
  }, [loading, user, router]);

  // Show loading state while auth is loading
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, show loading while redirecting
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // User is authenticated and has account access, show dashboard

  return (
    <div className="min-h-screen bg-background">
      {/* Collapsible Sidebar */}
      <CollapsibleSidebar />

      {/* Main content */}
      <div className="min-h-screen lg:pl-16">
        <header className="flex h-16 items-center gap-4 border-b bg-white px-4 sm:px-6 lg:px-6">
          <div className="flex-1 lg:ml-0 ml-12">
            <h1 className="text-base sm:text-lg font-semibold">Dashboard</h1>
            {user && (
              <p className="text-xs sm:text-sm text-muted-foreground">
                Welcome, {user.user_metadata?.first_name || user.name?.split(" ")[0] || user.name || "User"}!
              </p>
            )}
          </div>
        </header>

        <main className="p-4 sm:p-6">
          {/* Main Dashboard Grid - Responsive Layout */}
          <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
            {/* Section 1: Production Flow Chart */}
            <ProductionFlowChart accountId={null} />

            {/* Section 2: Production Progress */}
            <ProductionProgress accountId={null} />

            {/* Section 3: Production Speed KPI */}
            <ProductionSpeedKPI accountId={null} />

            {/* Section 4: Productivity Leaderboard */}
            <DailyProductivitySummary accountId={null} />
          </div>
        </main>
      </div>
    </div>
  );
}
